/* ============================================================================
   DATA — the page's content, by gender.
   Figma (3468:242337) designs the Women's state of the three gendered
   sections and fills in two core styles (Catarina, Isabel); everything else
   is real wearfigs.com content exported with figs-product-data into
   data/women.json and data/men.json (ghosts per colour, Quick Buy galleries,
   sizes), with the FIBREx Fall Dropbox shots where they're better.
   Figma's own plates win where Figma has them.
   ========================================================================== */

const files = import.meta.glob('./data/*.json', { eager: true, import: 'default' });
const json = (g) => files[`./data/${g}.json`] || null;

export const GENDERS = ['women', 'men'];

/* Our Core Products (V1) — Figma's Women's cards and the Men's same pattern,
   exported by scripts/carousel-data.mjs: name, colour, price, four photos,
   sizes, fits */
const carousel = files['./data/carousel.json'] || {};
export const CAROUSEL = { women: carousel.women || [], men: carousel.men || [] };
export const slug = (s) => String(s || '').toLowerCase().replace(/™|®/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

/* ---- links into the rest of the lab (src/figs-nav/figs-nav.js LAB) ---- */
export const LAB = {
  homepage: '/experiments/08-d-homepage/',
  scrubPants: '/experiments/03-plp-prototype/',
  classicFit: '/experiments/11-plp-classic-fit/',
  menCore: '/experiments/12-plp-core/',
  scrubSets: '/experiments/14-plp-scrub-sets/',
  mensSet: '/experiments/10-pdp-prototype/mens-set.html',
  topPdp: '/experiments/10-pdp-prototype/',
  pantPdp: '/experiments/10-pdp-prototype/pants.html',
  builder: '/experiments/15-uniform-builder/',
  charcoal: '/experiments/13-plp-charcoal/',
};
/* a product the lab has a PDP prototype for opens it; the rest open live */
export function productHref(p, color) {
  const h = p?.handle || '';
  const c = color ? `?color=${color}` : '';
  if (h === 'womens-catarina-scrub-top') return LAB.topPdp + c;
  if (h === 'womens-isabel-scrub-pants') return LAB.pantPdp + c;
  return p?.url || '#';
}

/* ============================================================
   OUR CATALOG — six tiles (Figma 3311:134742 for Women)
   ============================================================ */
const CATALOG_WOMEN = [
  { slug: 'scrub-tops', label: 'scrub tops', image: 'assets/figma/cat-scrub-tops.webp', tone: 'light', href: LAB.classicFit },
  { slug: 'scrub-pants', label: 'scrub pants', image: 'assets/figma/cat-scrub-pants.webp', tone: 'light', href: LAB.scrubPants, crop: { w: 142.68, h: 160.14, l: -29.14, t: -54.02 } },
  { slug: 'underscrubs', label: 'underscrubs', image: 'assets/figma/cat-underscrubs.webp', tone: 'dark', href: '#' },
  { slug: 'outerwear', label: 'outerwear', image: 'assets/figma/cat-outerwear.webp', tone: 'dark', href: '#', crop: { w: 110.78, h: 120.27, l: 0.01, t: -10.02 } },
  { slug: 'accessories', label: 'accessories', image: 'assets/figma/cat-accessories.webp', tone: 'dark', href: '#' },
  { slug: 'scrub-sets', label: 'scrub sets', image: 'assets/figma/cat-scrub-sets.webp', tone: 'dark', href: LAB.scrubSets },
];
const MEN_CATALOG_HREFS = { 'scrub-tops': LAB.menCore, 'scrub-pants': LAB.menCore, 'scrub-sets': LAB.mensSet };

/* ============================================================
   COLOR IS EVERYTHING WE DO — the catalog's component again
   (D 3774:13034 · M 3774:13086), tabbed Core / Limited Edition.
   Core is Figma's six plates (Charcoal and Moss with their baked
   labels painted out, media-src/colors); Limited Edition isn't
   designed yet, so it's six current drops, newest first, from the
   Catarina™ gallery on wearfigs.com, each set on a backdrop in its own
   garment colour (scripts/color-splash.py) so every card immerses the
   way Figma's Navy / Burgundy / Moss plates do. A card opens that colour's
   wearfigs.com collection for the page's gender; Charcoal for
   Women opens the lab's Charcoal PLP.
   ============================================================ */
const COLOR_LINES = {
  core: [
    { slug: 'black', label: 'black' },
    { slug: 'navy', label: 'navy', tone: 'dark' },
    { slug: 'burgundy', label: 'burgundy', tone: 'dark' },
    { slug: 'graphite', label: 'graphite', tone: 'dark' },
    { slug: 'charcoal', label: 'charcoal', tone: 'dark' },
    { slug: 'moss', label: 'moss', tone: 'dark' },
  ],
  /* white labels on the mid and dark backdrops, ink on the two light ones
     (as Figma's Black plate) */
  limited: [
    { slug: 'bonsai', label: 'bonsai', tone: 'dark' },
    { slug: 'dark-acai', label: 'dark açaí', tone: 'dark' },
    { slug: 'espresso', label: 'espresso', tone: 'dark' },
    { slug: 'smokey-taupe', label: 'smokey taupe', tone: 'dark' },
    { slug: 'fireside', label: 'fireside' },
    { slug: 'lagoon', label: 'lagoon' },
  ],
};
export const COLOR_LINE_LABELS = { core: 'core', limited: 'limited edition' };
export function colorsFor(line, g) {
  return (COLOR_LINES[line] || COLOR_LINES.core).map((c) => ({
    ...c,
    image: `assets/colors/${c.slug}.webp`,
    href: c.slug === 'charcoal' && g === 'women' ? LAB.charcoal
      : `https://www.wearfigs.com/collections/${c.slug}-scrubs-${g === 'men' ? 'mens' : 'womens'}`,
  }));
}

/* ============================================================
   MEET OUR CORE LINE — Figma's two filled variants, then the
   rest from wearfigs.com (women.json / men.json `core`)
   ============================================================ */
const CORE_FIGMA = {
  catarina: {
    heading: 'Catarina Scrub Top', colorCount: 29, pockets: '1 pocket', fabric: 'FIONx fabric',
    description: 'Simple and clean, but far from basic. The FIONx Catarina™ has a flattering V-neck, single chest pocket, classic fit and our proprietary FIONx™ fabric. Ta-da',
    plates: {
      first: { kind: 'video', src: 'assets/media/catarina-spin.mp4', poster: 'assets/media/catarina-spin-poster.webp', bg: 'spin' },
      detail: { src: 'assets/figma/core-catarina-detail.webp' },
      model: { src: 'assets/figma/core-catarina-model.webp', crop: { w: 122.1, h: 122.14, l: -7.92, t: -4.16 } },
    },
  },
  isabel: {
    heading: 'Isabel High-Rise Wide-Leg Scrub Pant', colorCount: 29, pockets: '8 pockets', fabric: 'FIONx fabric',
    description: 'Go wide with on-the-pulse appeal. The Isabel High-Rise Wide-Leg Scrub Pant features a roomy silhouette with eight pockets and a super comfy waistband.',
    plates: {
      first: { kind: 'ghost', src: 'assets/figma/core-isabel-ghost.webp' },
      detail: { src: 'assets/figma/core-isabel-detail.webp' },
      model: { src: 'assets/figma/core-isabel-model.webp', fit: 'contain', bg: '#8d8d8d' },
    },
    ghost: 'assets/figma/core-isabel-ghost.webp',
  },
};
const CORE_WOMEN_ORDER = [
  ['tops', 'catarina', 'Catarina'], ['tops', 'rafaela', 'Rafaela'], ['tops', 'casma', 'Casma'], ['tops', 'montex', 'Montex'],
  ['pants', 'isabel', 'Isabel'], ['pants', 'kade', 'Kade'], ['pants', 'zamora', 'Zamora'], ['pants', 'livingston', 'Livingston'], ['pants', 'yola', 'Yola'],
];

/* ============================================================
   FABRIC — Figma's FIONx / FIBREx variants, then FORMx™ and
   FlexKnit™ from wearfigs.com/pages/figs-anatomy and their fabric and care.
   `loop` is the ChatGPT-made motion plate (assets/fabric/);
   until it exists the plate is the still.
   ============================================================ */
export const FABRICS = [
  {
    id: 'fionx', name: 'fionx', tm: true, tone: '#2a0f14',
    body: 'The fabric that started it all, engineered for everyday excellence. Lightweight, breathable, and ridiculously soft.',
    composition: ['72% Polyester', '21% Rayon', '7% Spandex'],
    properties: ['Stretch', 'Anti-Microbial', 'Moisture-Wicking'],
    still: 'assets/figma/fabric-fionx.webp', stillMobile: 'assets/figma/fabric-fionx-mobile.webp', thumb: 'assets/figma/fabric-fionx.webp',
    loop: 'assets/fabric/fionx-loop', logo: { src: 'assets/figma/fabric-fionx-logo.svg', w: 200, h: 87, inset: [27.16, 15.51, 26.38, 15.03] },
    shop: { women: LAB.classicFit, men: LAB.menCore },
  },
  {
    id: 'fibrex', name: 'fibrex', tm: true, isNew: true, tone: '#0f1024',
    body: 'A versatile fabric designed for do-it-all durability. Advanced technical stretch meets stylish structure.',
    composition: ['55% Recycled Polyester', '45% Polyester'],
    properties: ['Lightweight', 'Anti-Microbial', 'Moisture-Wicking', 'Structured', 'Durable'],
    still: 'assets/figma/fabric-fibrex.webp', thumb: 'assets/figma/fabric-fibrex.webp', thumbCrop: { w: 121.53, h: 121.98, l: -21.53, t: -13.73 },
    loop: 'assets/fabric/fibrex-loop', logo: { src: 'assets/figma/fabric-fibrex-logo.png', w: 200, h: 66 },
    shop: { women: LAB.classicFit, men: LAB.menCore },
  },
  {
    id: 'formx', name: 'formx', tm: true, tone: '#16224a',
    body: 'An elevated fabric crafted for high-performance polish and support. Sculptural stretch meets refined all-day comfort.', // figs-anatomy
    composition: ['81% Nylon', '19% Spandex'],
    properties: ['Ultra-Stretch', 'Moisture-Wicking', 'Anti-Static', 'Anti-Microbial'],
    still: 'assets/figma/fabric-tab3.webp', thumb: 'assets/figma/fabric-tab3.webp',
    loop: 'assets/fabric/formx-loop', word: 'form',
    shop: { women: LAB.classicFit, men: LAB.menCore },
  },
  {
    /* wearfigs.com/pages/figs-anatomy "FlexKnit™" tile + the Dawes ¼ Zip's fabric
       and care; men's only on the site today (the women's collection redirects) */
    id: 'flexknit', name: 'flexknit', tm: true, tone: '#1d2a24',
    body: 'Airy knit piqué designed for effortless performance. Soft, structured, and durable, with made-to-move stretch.',
    composition: ['88% Nylon', '12% Spandex'],
    properties: ['Four-Way Stretch', 'Anti-Microbial', 'Moisture-Wicking', 'Enhanced Airflow'],
    still: 'assets/figma/fabric-tab4.webp', thumb: 'assets/figma/fabric-tab4.webp',
    loop: 'assets/fabric/flexknit-loop', word: 'flexknit',
    logo: null, // assets/figma/fabric-flexknit-logo.* from Dropbox, when it's in (fabric.js picks it up)
    shop: { men: 'https://www.wearfigs.com/collections/flexknit-scrubs-mens' },
  },
];

/* ============================================================
   DEFINE YOUR UNIFORM — the table (3404:239159): systems ×
   layers, Figma's women's grid. Swatches: the eight core colours.
   ============================================================ */
export const SYSTEMS = [
  { id: 'core', label: '01 Core', cols: [1, 4], avail: 'always' },
  { id: 'layering', label: '02 Layering System', cols: [5, 9], avail: 'percent' },
  { id: 'offshift', label: '03 Off Shift', cols: [10, 11], avail: null },
];
export const ROWS = [
  { id: 'tops', label: 'Α. scrub tops' },
  { id: 'pants', label: 'Β. scrub pants' },
  { id: 'underscrubs', label: 'Δ. underscrubs' },
  { id: 'accessories', label: 'Ε. accessories' },
];
export { ownBase };

/* ============================================================
   PAIRINGS — underscrubs and accessories are worn WITH the uniform, and
   are rarely made in its exact colour, so each core colour lists its
   complements, best first. Neutrals carry everything (colour theory: a
   saturated or deep scrub reads cleanest against black, white, or grey);
   the order follows wearfigs.com's "Wear It With" on the Catarina, Zamora,
   Leon, and Cairo PDPs (surveyed 2026-10-22) — optic-white socks with navy
   and royal, black underscrubs and socks with white, grey with charcoal —
   and the brief: black under navy, charcoal and greys with mauve, whites
   with the blues. The exact colour is left out on purpose.
   ============================================================ */
const UNDER = {
  black: ['charcoal', 'graphite', 'white'],
  graphite: ['black', 'charcoal', 'white'],
  navy: ['black', 'charcoal', 'graphite'],
  charcoal: ['black', 'graphite', 'white'],
  white: ['black', 'graphite', 'charcoal'],
  'royal-blue': ['white', 'ceil-blue', 'black'],
  'ceil-blue': ['white', 'navy', 'graphite'],
  mauve: ['charcoal', 'graphite', 'black'],
  burgundy: ['black', 'charcoal', 'graphite'],
};
const ACCS = {
  black: ['graphite', 'white', 'charcoal'],
  graphite: ['black', 'charcoal', 'white'],
  navy: ['black', 'white', 'charcoal'],
  charcoal: ['black', 'graphite', 'white'],
  white: ['black', 'navy', 'graphite'],
  'royal-blue': ['white', 'navy', 'black'],
  'ceil-blue': ['white', 'navy', 'graphite'],
  mauve: ['charcoal', 'graphite', 'black'],
  burgundy: ['black', 'charcoal', 'graphite'],
};
const pairs = Object.fromEntries(Object.keys(UNDER).map((c) => [c, { underscrubs: UNDER[c], accessories: ACCS[c] }]));
/* men's royal blue takes black before ceil (Makato isn't made in ceil; the
   site pairs it black) */
export const PAIRINGS = {
  women: pairs,
  men: { ...pairs, 'royal-blue': { underscrubs: ['white', 'black', 'navy'], accessories: ACCS['royal-blue'] } },
};
export const systemOfCol = (c) => SYSTEMS.find((s) => c >= s.cols[0] && c <= s.cols[1]);

const SWATCHES_FIGMA = [
  { slug: 'black', name: 'Black', hex: '#2b2c30' },
  { slug: 'graphite', name: 'Graphite', hex: '#8d8d96' },
  { slug: 'navy', name: 'Navy', hex: '#2b2e43' },
  { slug: 'charcoal', name: 'Charcoal', hex: '#46444c' },
  { slug: 'white', name: 'White', hex: '#ffffff' },
  { slug: 'royal-blue', name: 'Royal Blue', hex: '#285ad3' },
  { slug: 'ceil-blue', name: 'Ceil Blue', hex: '#5b7ebd' },
  { slug: 'mauve', name: 'Mauve', hex: '#885157' },
];
/* The recolour target per colour: the median garment colour of the real
   wearfigs.com ghosts in it (scripts/ghost-tints.py, alpha-weighted, labels
   out) — what a real render averages to, so a recoloured navy render sits
   beside real ones without looking darker or flatter. */
export const GHOST_TINTS = {
  black: '#222222', graphite: '#747478', navy: '#26263a', charcoal: '#49444d',
  white: '#bebcbe', 'royal-blue': '#0262d2', 'ceil-blue': '#83b0e1', mauve: '#bc7a7e',
};

const DYU_WOMEN_FIGMA = [
  ['tops', 1, 'Catarina One-Pocket'], ['tops', 2, 'Casma Three-Pocket'], ['tops', 3, 'Montex Mandarin Collar'], ['tops', 4, 'Rafaela Oversized'],
  ['tops', 7, 'Florence Henley SS Scrub Top'], ['tops', 8, 'Sona Slim Scrub Top'], ['tops', 9, 'Camrose Mock-Neck Scrub Top'],
  ['pants', 1, 'Zamora Jogger'], ['pants', 2, 'Kade Cargo'], ['pants', 3, 'Isabel Wide Leg'], ['pants', 4, 'Livingston Basic'],
  ['pants', 5, 'Yola Skinny'], ['pants', 6, 'Allison High-Rise Ultra-Wide-Leg', true], ['pants', 7, 'Rochelle Wide-Leg', true],
  ['pants', 8, 'Roslyn Wide-Leg'], ['pants', 9, 'Uman High-Rise Jogger'], ['pants', 10, 'Off-Shift Wide-Leg Sweatpants™'],
  ['pants', 11, 'Hayden Vintage Wash Sweatpants'],
  ['underscrubs', 1, 'Salta SS Underscrub'], ['underscrubs', 2, 'Salta LS Underscrub'], ['underscrubs', 5, 'Palmer Ribbed Long-Sleeve'],
  ['accessories', 5, 'Scrub Cap'], ['accessories', 6, 'Cinched Headband'], ['accessories', 7, 'FIGS | ArchTek® Ankle Socks'],
  ['accessories', 8, 'FIGS | ArchTek® Compression Socks'], ['accessories', 9, 'The Shift Tote'], ['accessories', 10, 'Scrunchie'],
];

/* ============================================================
   ASSEMBLY — one object per gender
   ============================================================ */
function coreFor(g, j) {
  const list = j?.core || [];
  const base = g === 'women'
    ? CORE_WOMEN_ORDER.map(([group, id, label]) => ({ group, id, label, ...(list.find((x) => x.id === id) || {}) }))
    : list.map((x) => ({ ...x }));
  return base.map((p) => {
    const fig = g === 'women' ? CORE_FIGMA[p.id] : null;
    const img = p.images || {};
    const plates = fig?.plates || {
      first: img.ghost ? { kind: 'ghost', src: img.ghost } : null,
      detail: img.detail ? { src: img.detail } : null,
      model: img.model ? { src: img.model } : null,
    };
    return {
      ...p,
      heading: fig?.heading || p.heading || `${p.label} ${p.group === 'tops' ? 'Scrub Top' : 'Scrub Pant'}`,
      colorCount: fig?.colorCount ?? p.colorCount,
      pockets: fig?.pockets || p.pockets,
      fabric: fig?.fabric || p.fabric || 'FIONx fabric',
      description: fig?.description || p.description || '',
      plates,
      ghost: fig?.ghost || img.ghost || null,
      href: productHref(p) || p.url || '#',
    };
  });
}

function dyuFor(g, j) {
  if (j?.dyu?.length) return j.dyu.map((p) => ({ ...p, href: productHref(p) }));
  if (g !== 'women') return [];
  /* before the export lands: Figma's names, no ghosts */
  return DYU_WOMEN_FIGMA.map(([row, col, name, isNew]) => ({ id: slug(`${name}`), row, col, name, isNew: !!isNew, colors: {}, sizes: [], types: [] }));
}

/* Define Your Uniform, as slots: each table position holds candidates in
   order of preference, and the page shows, per colour, the first one with a
   real ghost in it, else the first made in it with its own render to
   recolour — else the cell stays empty (no on-model photos, no stand-ins).
   `dyu2` (slots + products) is the export's shape; the first export's flat
   `dyu` list reads as one candidate per slot. */
const realGhost = (p) => Object.values(p.colors || {}).some((c) => c?.ghost);
const ownBase = (p) => p.colors?.navy?.ghost || (p.navyGhost?.path && !['proxy', 'none'].includes(p.navyGhost.kind) ? p.navyGhost.path : null)
  || Object.values(p.colors || {}).find((c) => c?.ghost)?.ghost || null;
function slotsFor(g, j) {
  if (j?.dyu2?.slots?.length) {
    const byId = j.dyu2.products || {};
    return j.dyu2.slots.map((s) => ({
      row: s.row, col: s.col,
      candidates: s.candidates.map((id) => byId[id] && { id, ...byId[id], href: productHref(byId[id]) }).filter((p) => p && (realGhost(p) || ownBase(p))),
    })).filter((s) => s.candidates.length);
  }
  return dyuFor(g, j).filter((p) => realGhost(p) || ownBase(p)).map((p) => ({ row: p.row, col: p.col, candidates: [p] }));
}

function catalogFor(g, j) {
  if (g === 'women') return CATALOG_WOMEN;
  const list = j?.catalog || [];
  return list.map((t) => ({ ...t, href: MEN_CATALOG_HREFS[t.slug] || '#' }));
}

export const DATA = Object.fromEntries(GENDERS.map((g) => {
  const j = json(g);
  return [g, {
    gender: g,
    ready: !!j,
    /* chips in Figma's product-colour tokens; the site's swatch hex tints the ghosts */
    swatches: j?.swatches?.length
      ? j.swatches.map((s) => {
        const sl = s.slug || slug(s.name);
        const fig = SWATCHES_FIGMA.find((f) => f.slug === sl);
        return { ...s, slug: sl, tint: GHOST_TINTS[sl] || s.hex, hex: fig?.hex || s.hex };
      })
      : SWATCHES_FIGMA,
    catalog: catalogFor(g, j),
    core: coreFor(g, j),
    dyu: dyuFor(g, j),
    dyuSlots: slotsFor(g, j),
    /* the curated export holds real ghosts only — no recoloured stand-ins */
    dyuStrict: !!j?.dyu2?.slots?.length,
  }];
}));
